import { isBlink } from '@common/isBlink';
import { DEVICE } from '@config-web';

import { Camera as ToucanCameraServerCamera, CameraBrowser as ToucanCameraServerBrowser } from './ToucanCameraServer';
import { Camera as WebcamCamera, CameraBrowser as WebcamCameraBrowser } from './Webcam';
import { Camera as WebGPhoto2Camera, CameraBrowser as WebGPhoto2CameraBrowser } from './WebGPhoto2';

const getCameraModules = (compatibilityMode = false) => [
  ...(DEVICE === 'ELECTRON' && !compatibilityMode ? [{ browser: ToucanCameraServerBrowser, item: ToucanCameraServerCamera }] : []),
  ...(DEVICE !== 'ELECTRON' || compatibilityMode ? [{ browser: WebcamCameraBrowser, item: WebcamCamera }] : []),
  ...(DEVICE === 'WEB' && isBlink() ? [{ browser: WebGPhoto2CameraBrowser, item: WebGPhoto2Camera }] : []),
];

let cachedCameras = {};
let cachedAvailableCameras = [];

export const getCameras = async (compatibilityMode = false) => {
  const availableCameras = [];

  const Cameras = getCameraModules(compatibilityMode);
  const camerasLists = await Promise.all(Cameras.map((camType) => camType?.browser?.getCameras() || []));

  for (let i = 0; i < camerasLists.length; i++) {
    const cameras = camerasLists[i];
    const camType = Cameras[i];
    for (const camera of cameras) {
      availableCameras.push({
        ...camera,
        id: `${camera.module}-${camera.deviceId}`,
        entityClass: camType?.item || null,
      });
    }
  }

  availableCameras.sort((a, b) => {
    if (a.module === 'GPHOTO2' && b.module !== 'GPHOTO2') return 1;
    if (a.module !== 'GPHOTO2' && b.module === 'GPHOTO2') return -1;
    return a.id.localeCompare(b.id);
  });

  cachedAvailableCameras = availableCameras;
  return availableCameras;
};

export const getCamera = (id) => {
  if (cachedCameras[id]) {
    return cachedCameras[id];
  }

  const cameras = cachedAvailableCameras || [];

  for (const camera of cameras) {
    if (id === camera.id) {
      const CameraClass = camera?.entityClass;
      cachedCameras[id] = new CameraClass(camera.deviceId, camera);
      return cachedCameras[id];
    }
  }

  return null;
};

const convertBufferToCanvas = (buffer, type = 'image/jpeg') =>
  new Promise((resolve, reject) => {
    const blob = new Blob([buffer], { type });
    const url = URL.createObjectURL(blob);
    const imgObj = new Image();

    imgObj.onerror = reject;
    imgObj.onload = function () {
      const tmpCanvas = document.createElement('canvas');
      tmpCanvas.height = this.naturalHeight;
      tmpCanvas.width = this.naturalWidth;
      const ctx = tmpCanvas.getContext('2d', { alpha: false });
      ctx.drawImage(imgObj, 0, 0, this.naturalWidth, this.naturalHeight);
      resolve(tmpCanvas);
    };
    imgObj.src = url;
  });

// "pictures" can be Canvas or Buffer
const mergePictures = async (pictures = []) => {
  const imageData = [];

  // Convert each picture to ImageData
  for (const pictData of pictures) {
    let canvas = pictData;

    if (!(pictData instanceof HTMLCanvasElement)) {
      canvas = await convertBufferToCanvas(pictData);
    }

    imageData.push({
      width: canvas.width,
      height: canvas.height,
      data: canvas.getContext('2d', { alpha: false }).getImageData(0, 0, canvas.width, canvas.height).data,
    });
  }

  // Control if all images have the same size
  if (new Set(imageData.map((e) => e.width)).length > 1 || new Set(imageData.map((e) => e.height)).length > 1) {
    throw new Error('MERGE_SIZE_INCONSISTENCIES');
  }

  // Define final width and height
  const width = imageData[0].width;
  const height = imageData[0].height;

  const nbImageData = imageData.length;
  const dataArray = new Uint8ClampedArray(width * height * 4);

  for (let x = 0; x < width; x++) {
    for (let y = 0; y < height; y++) {
      const redIdx = y * (width * 4) + x * 4;
      const greenIdx = y * (width * 4) + x * 4 + 1;
      const blueIdx = y * (width * 4) + x * 4 + 2;
      const alphaIdx = y * (width * 4) + x * 4 + 3;

      let redValue = 0;
      let greenValue = 0;
      let blueValue = 0;

      for (let i = 0; i < nbImageData; i++) {
        redValue += imageData[i].data[redIdx];
        greenValue += imageData[i].data[greenIdx];
        blueValue += imageData[i].data[blueIdx];
      }
      dataArray[redIdx] = ~~(redValue / nbImageData);
      dataArray[greenIdx] = ~~(greenValue / nbImageData);
      dataArray[blueIdx] = ~~(blueValue / nbImageData);
      dataArray[alphaIdx] = 255;
    }
  }

  const imgValues = new ImageData(dataArray, width, height);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { alpha: false });
  ctx.putImageData(imgValues, 0, 0, 0, 0, width, height);

  return canvas;
};

const reversePicture = async (picture, reverseX = false, reverseY = false) => {
  let source = picture;
  if (!(source instanceof HTMLCanvasElement)) {
    source = await convertBufferToCanvas(source);
  }

  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = source.width;
  outputCanvas.height = source.height;
  const ctx = outputCanvas.getContext('2d', { alpha: false });

  ctx.save();
  ctx.scale(reverseX ? -1 : 1, reverseY ? -1 : 1);
  ctx.translate(reverseX ? -source.width : 0, reverseY ? -source.height : 0);
  ctx.drawImage(source, 0, 0);
  ctx.restore();

  return outputCanvas;
};

const canvasToArrayBuffer = (canvas, mimeType = `image/png`) =>
  new Promise((resolve) => {
    canvas?.toBlob(
      async (blob) => {
        return resolve(Buffer.from(await blob.arrayBuffer()));
      },
      mimeType,
      1
    );
  });

export const takePicture = async (camera, nbFramesToTake = 1, reverseX = true, reverseY = false) => {
  // eslint-disable-line no-async-promise-executor
  const bufferList = [];

  // Take pictures
  for (let i = 0; i < nbFramesToTake || i < 1; i++) {
    const data = await camera.takePicture();
    if (data) {
      bufferList.push(data);
    }
  }

  // Output frame
  let finalCanvas = bufferList?.[0];

  // Merge frames
  if (bufferList.length > 1) {
    finalCanvas = { type: 'image/png', buffer: await mergePictures(bufferList.map((e) => e?.buffer)) };
  }

  // Reverse frame
  if (reverseX || reverseY) {
    finalCanvas = { type: 'image/png', buffer: await reversePicture(finalCanvas.buffer, reverseX, reverseY) };
  }

  // Canvas to buffer
  if (finalCanvas?.buffer instanceof HTMLCanvasElement) {
    finalCanvas.buffer = await canvasToArrayBuffer(finalCanvas.buffer, finalCanvas.type);
  }

  // Return data
  let outputBuffer = Buffer.from(finalCanvas?.buffer);
  outputBuffer.type = finalCanvas?.type;
  return outputBuffer;
};
